import * as React from "react"
import { useTranslation } from "react-i18next"
import { clx } from "../utils/clx"
import { Button, MinusMiniIcon, ChevronLeftIcon, ChevronRightIcon } from "~/design-system"

/**
 * This component is based on the table element and its various children:
 *
 * - `Table`: `table`
 * - `Table.Header`: `thead`
 * - `Table.Row`: `tr`
 * - `Table.HeaderCell`: `th`
 * - `Table.Body`: `tbody`
 * - `Table.Cell`: `td`
 *
 * Each component supports the props or attributes of its equivalent HTML element.
 */
const Root = React.forwardRef<
  HTMLTableElement,
  React.TableHTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <table
    ref={ref}
    className={clx("text-ui-fg-base txt-base-regular-plus w-full", className)}
    {...props}
  />
))
Root.displayName = "Table"

const Row = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={clx(
      "group/row bg-ui-bg-base hover:bg-ui-bg-base-hover border-ui-border-subtle hover:border-transparent transition-fg border-b",
      "[&_td:last-child]:pr-6 [&_th:last-child]:pr-6 [&_th:last-child]:rounded-r-md",
      "[&_td:first-child]:pl-6 [&_th:first-child]:pl-6 [&_th:first-child]:rounded-l-md",
      className
    )}
    {...props}
  />
))
Row.displayName = "Table.Row"

const Cell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td ref={ref} className={clx("h-12 pr-3 whitespace-nowrap", className)} {...props} />
))
Cell.displayName = "Table.Cell"

const Header = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={clx(
      "[&_tr]:bg-grey-10 [&_tr:hover]:bg-grey-10 text-ui-fg-muted",
      className
    )}
    {...props}
  />
))
Header.displayName = "Table.Header"

const HeaderCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th ref={ref} className={clx("h-10 pr-3 text-left txt-base-regular-plus whitespace-nowrap", className)} {...props} />
))
HeaderCell.displayName = "Table.HeaderCell"

const Body = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={clx("border-ui-border-subtle border-b", className)}
    {...props}
  />
))
Body.displayName = "Table.Body"


interface TablePaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  count: number
  pageSize: number
  pageIndex: number
  pageCount: number
  canPreviousPage: boolean
  canNextPage: boolean
  translations?: {
    of?: string
    results?: string
    pages?: string
    prev?: string
    next?: string
  }
  previousPage: () => void
  nextPage: () => void
}

/**
 * This component is based on the `div` element and supports all of its props
 */
const Pagination = React.forwardRef<HTMLDivElement, TablePaginationProps>(
  (
    {
      className,
      /**
       * The total number of items.
       */
      count,
      /**
       * The number of items per page.
       */
      pageSize,
      /**
       * The total number of pages.
       */
      pageCount,
      /**
       * The current page index.
       */
      pageIndex,
      /**
       * Whether there's a previous page that can be navigated to.
       */
      canPreviousPage,
      /**
       * Whether there's a next page that can be navigated to.
       */
      canNextPage,
      /**
       * A function that handles navigating to the next page.
       * This function should handle retrieving data for the next page.
       */
      nextPage,
      /**
       * A function that handles navigating to the previous page.
       * This function should handle retrieving data for the previous page.
       */
      previousPage,
      ...props
    }: TablePaginationProps,
    ref
  ) => {
    const { t } = useTranslation()
    const { from, to } = React.useMemo(() => {
      const from = count === 0 ? count : pageIndex * pageSize + 1
      const to = Math.min(count, (pageIndex + 1) * pageSize)

      return { from, to }
    }, [count, pageIndex, pageSize])

    return (
      <div
        ref={ref}
        className={clx(
          "text-ui-fg-muted txt-compact-small-plus font-jakarta flex w-full items-center justify-between px-6 py-4",
          className
        )}
        {...props}
      >
        <div className="inline-flex items-center gap-x-1 px-3 py-[5px]">
          <p>{from}</p>
          <MinusMiniIcon className="text-ui-fg-muted" />
          <p>{t("{{to}} of {{count}} results", { to, count })}</p>
        </div>
        <div className="flex items-center gap-x-2">
          <Button
            type="button"
            variant="secondary"
            onClick={previousPage}
            disabled={!canPreviousPage}
            className="px-0 w-7 h-7 text-grey-50"
          >
            <ChevronLeftIcon size={16} />
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={nextPage}
            disabled={!canNextPage}
            className="px-0 w-7 h-7 text-grey-50"
          >
            <ChevronRightIcon className="text-grey-50" size={16} />
          </Button>
          <div className="inline-flex items-center gap-x-1 px-3 py-[5px]">
            <p>
              {t("{{pageIndex}} of {{pageCount}} pages", { pageIndex: pageIndex + 1, pageCount: Math.max(pageCount, 1) })}
            </p>
          </div>
        </div>
      </div>
    )
  }
)
Pagination.displayName = "Table.Pagination"

const Table = Object.assign(Root, {
  Row,
  Cell,
  Header,
  HeaderCell,
  Body,
  Pagination,
})

export { Table }
